/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import { map, concat, forEach, has, isUndefined, omit, sumBy, toNumber } from 'lodash';
import { Pricing } from 'blb-types/pricing';
/**
 * Returns a name property for a given FieldArray item
 * example: getIndexedFieldArrayName('repeatedItem', 3) --> 'repeatedItem[3]'
 */
export const getIndexedFieldArrayName = (field, index) => {
  return `${field}[${index}]`;
};
export const getFeeIndexById = (formSubsection, id) => {
  return formSubsection.findIndex(fee => fee.id === id);
};
const generateDropdownActionConfig = (rowActionConfigs, subsectionKey, setCurrentRequest) => {
  const actionConfig = {
    columnWidth: 4,
    dropdownActions: [],
    type: 'EllipsisDropdown'
  };
  forEach(rowActionConfigs, _ref => {
    let {
      type
    } = _ref;
    switch (type) {
      case 'Propose Edit':
        actionConfig.dropdownActions.push({
          clickHandler: rowIdx => setCurrentRequest({
            displayModal: true,
            rowIdx,
            subsectionKey,
            type: 'edit'
          }),
          icon: 'edit',
          label: 'Propose edit'
        });
        break;
      default:
        // Delete Row
        actionConfig.dropdownActions.push({
          clickHandler: rowIdx => setCurrentRequest({
            displayModal: true,
            rowIdx,
            subsectionKey,
            type: 'delete'
          }),
          icon: 'delete',
          label: 'Propose delete'
        });
    }
  });
  return actionConfig;
};
export const generateLedgerSubsectionRowActionConfigs = (rowActionConfigs, subsectionKey, setCurrentRequest) => {
  return map(rowActionConfigs, rowActionConfig => {
    // multiple actions, use the dropdown component
    return generateDropdownActionConfig(rowActionConfig, subsectionKey, setCurrentRequest);
  });
};

// Fees Aggregation

export const isFeeApprovedAndDeleted = fee => fee.status === Pricing.FeeStatus.APPROVED && fee.updateType === Pricing.UpdateType.DELETE;
export const aggregateFeesBySubsection = (formValues, aggregateConfigs, filterFunction) => {
  let aggregation = 0;
  forEach(aggregateConfigs, _ref2 => {
    let {
      subsectionKey,
      aggregateKey,
      isNegative
    } = _ref2;
    const subsectionValue = formValues[subsectionKey] || [];
    const subsectionSum = sumBy(subsectionValue, fee => {
      // ignore fee if it doesn't pass the filterFunction check
      if (filterFunction && !filterFunction(fee) || isFeeApprovedAndDeleted(fee)) {
        return 0;
      }
      return !isUndefined(fee[aggregateKey]) ? toNumber(fee[aggregateKey]) : 0;
    });
    aggregation += isNegative ? -subsectionSum : subsectionSum;
  });
  return aggregation;
};

// Initialization

export const formatClosingFeesResponse = response => {
  const {
    fees
  } = response;
  const formattedValue = Object.values(Pricing.LoanEstimateSectionTypes).reduce((acc, loanEstimateSectionType) => {
    acc[loanEstimateSectionType] = [];
    return acc;
  }, {});
  forEach(fees, fee => {
    if (!!fee.loanEstimateSection && has(formattedValue, fee.loanEstimateSection)) {
      formattedValue[fee.loanEstimateSection].push(fee);
    }
  });
  return formattedValue;
};
export const getFeesUpdated = formValues => {
  return Object.values(formValues).reduce((acc, feeFormValues) => {
    const updatedFeeFormValues = feeFormValues.filter(feeFormValue => feeFormValue.moreInfoRequested);
    return acc + updatedFeeFormValues.length;
  }, 0);
};
const sanitizeFeesForUpdate = (feeFormValues, feeOverride) => {
  return feeFormValues.map(feeFormValue => {
    const result = omit(feeFormValue, ['newNote', 'previousApprovedFee']);
    result.amount = toNumber(feeFormValue.amount);
    return {
      ...result,
      ...feeOverride
    };
  });
};
export const generateFeesPayloadForUpdate = function (formValues) {
  let feeOverride = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  const fees = Object.entries(formValues).reduce((acc, _ref3) => {
    let [key, feeFormValues] = _ref3;
    const sectionFeeOverride = key === Pricing.LoanEstimateSectionTypes.Payoffs ? {
      ...feeOverride,
      paidBy: 'Other'
    } : feeOverride;
    return concat(acc, sanitizeFeesForUpdate(feeFormValues, sectionFeeOverride)); // not possible that feeFormValues is undefined
  }, []);
  return fees;
};
export const setIdentifier = (identifier, err) => {
  err.identifier = identifier;
  throw err;
};